import api from './api';
import { Plan, Subscription, Invoice, ApiResponse } from '../types';

interface CreateSubscriptionData {
  plan_id: string;
  billing_cycle: 'monthly' | 'yearly';
  currency?: 'brl' | 'usd';
}

export const subscriptionService = {
  async getPlans(): Promise<ApiResponse<Plan[]>> {
    const response = await api.get('/subscription/plans');
    return response.data;
  },

  async getCurrentSubscription(): Promise<ApiResponse<Subscription>> {
    const response = await api.get('/subscription/current');
    return response.data;
  },

  async createSubscription(data: CreateSubscriptionData): Promise<ApiResponse<{ url?: string; sessionId?: string }>> {
    const response = await api.post('/subscription/checkout', {
      planId: data.plan_id,
      billingCycle: data.billing_cycle,
      currency: data.currency || 'brl'
    });
    return response.data;
  },

  async createCheckoutSession(planId: string, billingCycle: 'monthly' | 'yearly'): Promise<ApiResponse<{ sessionId: string; url: string }>> {
    const response = await api.post('/subscription/checkout', { planId, billingCycle });
    return response.data;
  },

  async getPortalUrl(): Promise<ApiResponse<{ url: string }>> {
    const response = await api.post('/subscription/portal');
    return response.data;
  },

  async createPortalSession(): Promise<ApiResponse<{ url: string }>> {
    const response = await api.post('/subscription/portal');
    return response.data;
  },

  async cancelSubscription(): Promise<ApiResponse<void>> {
    const response = await api.post('/subscription/cancel');
    return response.data;
  },

  async reactivateSubscription(): Promise<ApiResponse<void>> {
    const response = await api.post('/subscription/reactivate');
    return response.data;
  },

  async changePlan(planId: string, billingCycle: 'monthly' | 'yearly', currency?: 'brl' | 'usd'): Promise<ApiResponse<void>> {
    const response = await api.post('/subscription/change-plan', { planId, billingCycle, currency: currency || 'brl' });
    return response.data;
  },

  async getInvoices(): Promise<ApiResponse<Invoice[]>> {
    const response = await api.get('/subscription/invoices');
    return response.data;
  },

  async getUsage(): Promise<ApiResponse<UsageSummary>> {
    const response = await api.get('/subscription/usage');
    return response.data;
  }
};

export interface UsageItem {
  current: number;
  limit: number | null;
  unlimited: boolean;
  remaining: number | null;
  percentage: number;
}

export interface UsageSummary {
  projects: UsageItem;
  campaigns: UsageItem;
  keywords: UsageItem;
  competitors: UsageItem;
  content_generations: UsageItem;
  team_members: UsageItem;
  automated_workflows: UsageItem;
  image_generations: UsageItem;
}

export type UsageResource = keyof UsageSummary;
